import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ServiceComparison = () => {
  const [selectedServices, setSelectedServices] = useState([]);
  const [businessType, setBusinessType] = useState('');
  const [budget, setBudget] = useState('');

  const services = [
    {
      id: 'social-media',
      name: 'Social Media Marketing',
      icon: 'Share2',
      description: 'Build brand awareness and engage your audience',
      startingPrice: '₹20,000/month',
      duration: '3-6 months',
      bestFor: ['Brand Awareness', 'Customer Engagement', 'Lead Generation'],
      deliverables: ['Content Strategy', 'Daily Posting', 'Community Management', 'Monthly Reports']
    },
    {
      id: 'web-development',
      name: 'Web Development',
      icon: 'Code',
      description: 'Professional websites that convert visitors to customers',
      startingPrice: '₹10,000',
      duration: '6-12 weeks',
      bestFor: ['Online Presence', 'Lead Generation', 'E-commerce'],
      deliverables: ['Custom Design', 'Responsive Development', 'CMS Setup', 'SEO Optimization']
    },
    {
      id: 'app-development',
      name: 'App Development',
      icon: 'Smartphone',
      description: 'Mobile apps that enhance customer experience',
      startingPrice: '₹50,000',
      duration: '3-6 months',
      bestFor: ['Customer Retention', 'Mobile Experience', 'Business Automation'],
      deliverables: ['UI/UX Design', 'Native Development', 'Testing', 'App Store Launch']
    }
  ];

  const businessTypes = [
    { value: 'startup', label: 'Startup', icon: 'Rocket' },
    { value: 'small-business', label: 'Small Business', icon: 'Building' },
    { value: 'enterprise', label: 'Enterprise', icon: 'Building2' },
    { value: 'ecommerce', label: 'E-commerce', icon: 'ShoppingCart' },
    { value: 'nonprofit', label: 'Non-profit', icon: 'Heart' },
    { value: 'agency', label: 'Agency', icon: 'Users' }
  ];

  const budgetRanges = [
    
    { value: 'under-10K', label: 'Under ₹10,000', range: '₹5K - ₹10K' },
    { value: '10K-25K', label: '₹10,000 - ₹25,000', range: '₹10K - ₹25K' },
    { value: '25K-50k', label: '₹25,000 - ₹50,000', range: '₹25K - ₹50K' },
    { value: '50k-100k', label: '₹50,000 - ₹100,000', range: '₹50K - ₹100K' },
    { value: 'over-100k', label: 'Over ₹100,000', range: '₹100K+' }
  ];

  const handleServiceToggle = (serviceId) => {
    setSelectedServices(prev => 
      prev?.includes(serviceId) 
        ? prev?.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const getRecommendation = () => {
    if (!businessType || !budget) return null;

    const recommendations = {
      'startup': {
        'under-10K': ['social-media'],
        '10K-25K': ['social-media', 'web-development'],
        '25K-50k': ['web-development', 'social-media'],
        '50k-100k': ['web-development', 'app-development'],
        'over-100k': ['web-development', 'app-development', 'social-media']
      },
      'small-business': {
        'under-10K': ['social-media'],
        '10K-25K': ['web-development'],
        '25K-50k': ['web-development', 'social-media'],
        '50k-100k': ['web-development', 'social-media', 'app-development'],
        'over-100k': ['web-development', 'app-development', 'social-media']
      },
      'enterprise': {
        'under-10K': ['social-media'],
        '10K-25K': ['web-development'],
        '25K-50k': ['web-development', 'social-media'],
        '50k-100k': ['web-development', 'app-development'],
        'over-100k': ['web-development', 'app-development', 'social-media']
      },
      'ecommerce': {
        'under-10K': ['social-media'],
        '10K-25K': ['web-development'],
        '25K-50k': ['web-development', 'social-media'],
        '50k-100k': ['web-development', 'app-development'],
        'over-100k': ['web-development', 'app-development', 'social-media']
      }
    };

    return recommendations?.[businessType]?.[budget] || [];
  };

  const recommendation = getRecommendation();

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Target" size={16} />
            <span>Service Comparison Tool</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
            Find Your Perfect Service Mix
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Not sure which services you need? Use our interactive tool to get personalized recommendations based on your business type and budget.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Service Selection */}
          <div>
            <h3 className="text-2xl font-bold text-foreground mb-8">Available Services</h3>
            
            <div className="space-y-6">
              {services?.map((service) => (
                <div 
                  key={service?.id}
                  className={`p-6 rounded-2xl border-2 brand-transition cursor-pointer ${
                    selectedServices?.includes(service?.id)
                      ? 'border-accent bg-accent/5' :'border-border bg-card hover:border-accent/50'
                  }`}
                  onClick={() => handleServiceToggle(service?.id)}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      selectedServices?.includes(service?.id)
                        ? 'bg-accent text-accent-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      <Icon name={service?.icon} size={24} />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-lg font-bold text-foreground">{service?.name}</h4>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-muted-foreground">Starting at</span>
                          <span className="font-bold text-accent">{service?.startingPrice}</span>
                        </div>
                      </div>
                      
                      <p className="text-muted-foreground mb-4">{service?.description}</p>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-foreground mb-2">Best For:</p>
                          <div className="space-y-1">
                            {service?.bestFor?.map((item, idx) => (
                              <div key={idx} className="flex items-center space-x-2">
                                <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                                <span className="text-sm text-muted-foreground">{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div>
                          <p className="text-sm font-medium text-foreground mb-2">Timeline:</p>
                          <p className="text-sm text-muted-foreground">{service?.duration}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Business Profile & Recommendations */}
          <div>
            <h3 className="text-2xl font-bold text-foreground mb-8">Tell Us About Your Business</h3>
            
            <div className="bg-card rounded-2xl p-8 brand-shadow">
              {/* Business Type Selection */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-foreground mb-4">
                  What type of business are you?
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {businessTypes?.map((type) => (
                    <button
                      key={type?.value}
                      onClick={() => setBusinessType(type?.value)}
                      className={`p-3 rounded-lg border brand-transition text-left ${
                        businessType === type?.value
                          ? 'border-accent bg-accent/5 text-accent' :'border-border hover:border-accent/50'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon name={type?.icon} size={20} />
                        <span className="font-medium">{type?.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Budget Selection */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-foreground mb-4">
                  What's your budget range?
                </label>
                <div className="space-y-3">
                  {budgetRanges?.map((range) => (
                    <button
                      key={range?.value}
                      onClick={() => setBudget(range?.value)}
                      className={`w-full p-3 rounded-lg border brand-transition text-left ${
                        budget === range?.value
                          ? 'border-accent bg-accent/5 text-accent' :'border-border hover:border-accent/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{range?.label}</span>
                        <span className="text-sm text-muted-foreground">{range?.range}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Recommendations */}
              {recommendation && recommendation?.length > 0 && (
                <div className="bg-gradient-to-r from-accent/10 to-accent/5 rounded-xl p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Icon name="Lightbulb" size={20} className="text-accent" />
                    <h4 className="font-bold text-foreground">Recommended for You</h4>
                  </div>
                  
                  <div className="space-y-3">
                    {recommendation?.map((serviceId, index) => {
                      const service = services?.find(s => s?.id === serviceId);
                      return (
                        <div key={serviceId} className="flex items-center justify-between p-3 bg-background rounded-lg">
                          <div className="flex items-center space-x-3">
                            <span className="w-6 h-6 bg-accent text-accent-foreground rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <span className="font-medium text-foreground">{service?.name}</span>
                          </div>
                          <span className="text-sm font-medium text-accent">{service?.startingPrice}</span>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="mt-6 pt-4 border-t border-accent/20">
                    <Button 
                      variant="default" 
                      fullWidth
                      className="bg-accent hover:bg-accent/90 text-accent-foreground"
                      iconName="Calendar"
                      iconPosition="left"
                    >
                      Book Consultation for Recommended Services
                    </Button>
                  </div>
                </div>
              )}

              {/* Manual Selection Summary */}
              {selectedServices?.length > 0 && (
                <div className="mt-8 p-6 bg-surface rounded-xl">
                  <h4 className="font-bold text-foreground mb-4">Your Selected Services</h4>
                  <div className="space-y-3">
                    {selectedServices?.map((serviceId) => {
                      const service = services?.find(s => s?.id === serviceId);
                      return (
                        <div key={serviceId} className="flex items-center justify-between">
                          <span className="font-medium text-foreground">{service?.name}</span>
                          <span className="text-accent font-bold">{service?.startingPrice}</span>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-border">
                    <Button 
                      variant="outline" 
                      fullWidth
                      iconName="MessageCircle"
                      iconPosition="left"
                    >
                      Discuss Selected Services
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceComparison;